import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Form, Typography, Tag, Tooltip } from 'antd';
import { UserOutlined, EditOutlined } from '@ant-design/icons';
import RichTextEditor from './RichTextEditor';

const { Text } = Typography;

const CollaborativeIdtField = ({
  name,
  label,
  form,
  collaboration,
  style = {}
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [lastUpdatedBy, setLastUpdatedBy] = useState(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const editorRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Get typing users for this field
  const typingUsers = collaboration?.getTypingUsers(name) || [];
  const isConnected = collaboration?.isConnected || false;

  // Handle field focus
  const handleFocus = useCallback(() => {
    setIsFocused(true);
    if (collaboration?.sendTypingIndicator) {
      collaboration.sendTypingIndicator(name, true);
    }
  }, [collaboration, name]);

  // Handle field blur
  const handleBlur = useCallback(() => {
    setIsFocused(false);
    if (collaboration?.sendTypingIndicator) {
      collaboration.sendTypingIndicator(name, false);
    }
  }, [collaboration, name]);

  // Handle content change with debounced collaboration update
  const handleChange = useCallback((value) => {
    // Update form immediately for responsive UI
    form.setFieldsValue({ [name]: value });

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Send update to other users after 1 second of no typing
    typingTimeoutRef.current = setTimeout(() => {
      if (collaboration?.sendFieldUpdate) {
        collaboration.sendFieldUpdate(name, value);
      }
    }, 1000);
  }, [form, name, collaboration]);

  // Handle external updates from other users
  useEffect(() => {
    const handleExternalUpdate = (fieldName, value, updatedBy, timestamp) => {
      if (fieldName === name && !isFocused) {
        // Only update if we're not currently editing this field
        form.setFieldsValue({ [name]: value });
        setLastUpdatedBy(updatedBy);
        setLastUpdatedAt(timestamp);
      }
    };

    // This would be called by the parent component when receiving updates
    // We'll handle this through the collaboration hook
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [name, form, isFocused]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Form.Item 
      name={name} 
      label={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <span>{label}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {/* Connection status indicator */}
            {isConnected && (
              <Tooltip title="Real-time collaboration active">
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#52c41a',
                  animation: 'pulse 2s infinite'
                }} />
              </Tooltip>
            )}
            
            {/* Typing indicators */}
            {typingUsers.length > 0 && (
              <div style={{ display: 'flex', gap: '4px' }}>
                {typingUsers.slice(0, 3).map((user, index) => (
                  <Tooltip key={user.userId} title={`${user.name} is typing...`}>
                    <Tag 
                      size="small" 
                      color={collaboration?.getUserColor(user.userId)}
                      icon={<EditOutlined />}
                      style={{ 
                        fontSize: '10px',
                        animation: 'blink 1s infinite',
                        margin: 0
                      }}
                    >
                      {user.name.split(' ')[0]}
                    </Tag>
                  </Tooltip>
                ))}
                {typingUsers.length > 3 && (
                  <Tooltip title={`${typingUsers.length - 3} more users typing...`}>
                    <Tag size="small" style={{ fontSize: '10px', margin: 0 }}>
                      +{typingUsers.length - 3}
                    </Tag>
                  </Tooltip>
                )}
              </div>
            )}
            
            {/* Last updated indicator */}
            {lastUpdatedBy && lastUpdatedAt && (
              <Tooltip title={`Last updated by ${lastUpdatedBy.name} at ${new Date(lastUpdatedAt).toLocaleTimeString()}`}>
                <Tag 
                  size="small" 
                  icon={<UserOutlined />}
                  style={{ 
                    fontSize: '10px',
                    backgroundColor: '#f0f0f0',
                    border: '1px solid #d9d9d9',
                    margin: 0
                  }}
                >
                  {lastUpdatedBy.name.split(' ')[0]}
                </Tag>
              </Tooltip>
            )}
          </div>
        </div>
      }
      style={style}
    >
      <div style={{ position: 'relative' }}>
        <RichTextEditor
          ref={editorRef}
          value={form.getFieldValue(name)}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          style={{
            border: isFocused && isConnected ? '2px solid #1890ff' : undefined,
            borderRadius: '6px',
            transition: 'border-color 0.3s ease'
          }}
        />
        
        {/* Active users indicator overlay */}
        {collaboration?.activeUsers && collaboration.activeUsers.length > 0 && (
          <div style={{
            position: 'absolute',
            top: '-8px',
            right: '-8px',
            display: 'flex',
            gap: '4px',
            zIndex: 10
          }}>
            {collaboration.activeUsers.slice(0, 3).map((user, index) => (
              <Tooltip key={user.userId} title={`${user.name} (${user.role}) is collaborating`}>
                <div style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  backgroundColor: collaboration.getUserColor(user.userId),
                  border: '2px solid white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '8px',
                  color: 'white',
                  fontWeight: 'bold',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}>
                  {user.name.charAt(0).toUpperCase()}
                </div>
              </Tooltip>
            ))}
            {collaboration.activeUsers.length > 3 && (
              <Tooltip title={`${collaboration.activeUsers.length - 3} more users active`}>
                <div style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  backgroundColor: '#666',
                  border: '2px solid white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '8px',
                  color: 'white',
                  fontWeight: 'bold',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}>
                  +{collaboration.activeUsers.length - 3}
                </div>
              </Tooltip>
            )}
          </div>
        )}
      </div>
    </Form.Item>
  );
};

export default CollaborativeIdtField;
